import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AddressFormValidations,
  ButtonWithSpinner,
  deliveryMethodHelpers,
  DeliveryMethodInfo,
  DeliveryMethodSelector,
  DeliveryRegionInfo,
  httpStatuses,
  Loading,
  PolicyRuleAlerts,
  policyRuleDisplayMessagePlacements,
  ToastContext,
} from '@reveni-io/reveni-ui'

import ConsentModal from '../components/ConsentModal/ConsentModal'
import ExchangeNotAllowedModal from '../components/ExchangeNotAllowedModal/ExchangeNotAllowedModal'
import PolicyRuleMessageWrapper from '../components/PolicyRuleMessageWrapper/PolicyRuleMessageWrapper'
import RegionsModal from '../components/RegionsModal/RegionsModal'
import Summary from '../components/Summary/Summary'
import AppContext from '../context/AppContext'
import useConsentModal from '../hooks/useConsentModal/useConsentModal'
import useDeliveryMethods from '../hooks/useDeliveryMethods/useDeliveryMethods'
import useRegionModal from '../hooks/useRegionModal/useRegionModal'
import useReturnGroup from '../hooks/useReturnGroup/useReturnGroup'
import useReturnLabels from '../hooks/useReturnLabels/useReturnLabels'
import useTransactionTypes from '../hooks/useTransactionTypes/useTransactionTypes'
import AddressSuggestionDrawer from './AddressSuggestionDrawer/AddressSuggestionDrawer'
import DropOffPointDrawer from './DropOffPointDrawer/DropOffPointDrawer'
import DropOffPointSelector from './DropOffPointSelector/DropOffPointSelector'
import useAddressForm from './hooks/useAddressForm'
import useAddressValidation from './hooks/useAddressValidation/useAddressValidation'
import useDropOffPoint from './hooks/useDropOffPoint/useDropOffPoint'
import ReturnGroupIn from './ReturnGroupIn/ReturnGroupIn'
import ReturnGroupOut from './ReturnGroupOut/ReturnGroupOut'
import ReturnLabelsSelector from './ReturnLabelsSelector/ReturnLabelsSelector'
import { loadingMessages } from './constants'
import {
  getDeliveryMethodCustomMessage,
  getExchangeAddressFlags,
  getPhoneNumber,
  getSelectedProvince,
  supportsDropOffPointSelection,
} from './helpers'
import { ActionsContainer } from './styles'
import * as Styles from './styles'

const DeliveryMethods = () => {
  const {
    deliveryMethods,
    deliveryMethod,
    updateAndSaveDeliveryMethodInContext,
    getDeliveryMethods,
    updateDeliveryMethods,
    specialConstraints,
    addDeliveryMethodAndGoToNextPage,
    addingDeliveryMethod,
    loading: fetchingDeliveryMethods,
  } = useDeliveryMethods()
  const { t, i18n } = useTranslation()
  const { setToastInfo } = useContext(ToastContext)
  const { context, setContext } = useContext(AppContext)
  const { showRegionModal, setShowRegionModal, getProvinces } = useRegionModal()
  const [selectedProvince, setSelectedProvince] = useState(context.orderInfo.shipping_address?.province?.id)
  const selectedCountry = context.orderInfo.shipping_address?.country
  const [showExchangeNotAllowedModal, setShowExchangeNotAllowedModal] = useState(false)
  const { changeNumberOfReturnLabels, numberOfReturnLabels, maxNumberOfReturnLabels } = useReturnLabels()
  const {
    loading: assigningToReturnGroup,
    leaveReturnGroup,
    joinReturnGroup,
    availableGroupAction,
  } = useReturnGroup({
    setToastInfo,
    getDeliveryMethods,
  })

  const { updateAddress, replaceAddress, handleChangeUseSameAddress, shippingAddress, provinces } = useAddressForm()
  const {
    validateAddress,
    validatingAddress,
    fieldErrors,
    clearFieldErrors,
    suggestedAddress,
    showSuggestion,
    closeSuggestion,
  } = useAddressValidation()
  const { getAddDeliveryMethodPayload } = deliveryMethodHelpers()
  const { consentModal, showConsentModal, askForConsent, confirmConsent, closeConsentModal } = useConsentModal(
    policyRuleDisplayMessagePlacements.returnMethodPage
  )
  const { getOrderTransactionType } = useTransactionTypes()
  const {
    showDropOffPointDrawer,
    openDropOffPointDrawer,
    closeDropOffPointDrawer,
    selectDropOffPoint,
    selectedDropOffPoint,
  } = useDropOffPoint()

  const deliveryMethodCustomMessage = getDeliveryMethodCustomMessage({
    customMessages: context.merchantInfo?.preferences?.custom_messages,
    language: i18n.language,
    deliveryMethodType: deliveryMethod?.type,
  })

  const showDropOffPointSelector = supportsDropOffPointSelection(deliveryMethod)
  const transactionType = getOrderTransactionType()
  const { isExchangeOrMixed, showExchangeAddressCheckbox, needsExchangeAddress } = getExchangeAddressFlags({
    transactionType,
    deliveryMethod,
    useSameAddressForExchange: context.orderInfo.use_same_address_for_exchange,
  })

  const showRegionModalWhenNoProvinceSelected = () => {
    if (!context.orderInfo.shipping_address?.province && context?.allowed_region?.provinces?.length > 0) {
      setShowRegionModal(true)
    }
  }

  useEffect(showRegionModalWhenNoProvinceSelected, [
    context?.allowed_region?.provinces?.length,
    context.orderInfo.shipping_address?.province,
    setShowRegionModal,
  ])

  const handleChangeDeliveryMethod = methodId => {
    const method = deliveryMethods?.find(m => m.id === methodId)
    updateAndSaveDeliveryMethodInContext(method)
    selectDropOffPoint(null)
    clearFieldErrors()
  }

  const handleConfirmDropOffPoint = dropOffPoint => {
    selectDropOffPoint(dropOffPoint)
    closeDropOffPointDrawer()
  }

  const onErrorAddingDeliveryMethod = error => {
    if (error?.response?.status === httpStatuses.forbidden) {
      setShowExchangeNotAllowedModal(true)
    } else {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const addDeliveryMethodAndContinue = orderInfo => {
    addDeliveryMethodAndGoToNextPage({
      returnId: context.orderInfo.returnId,
      payload: getAddDeliveryMethodPayload(orderInfo),
      isExchangeOrMixed,
      needsExchangeAddress,
      onError: onErrorAddingDeliveryMethod,
    })
  }

  const handleContinue = async () => {
    if (askForConsent()) return

    const canContinue = await validateAddress()

    if (!canContinue) return

    addDeliveryMethodAndContinue(context.orderInfo)
  }

  const handleUpdateAddress = event => {
    clearFieldErrors()
    updateAddress(event)
  }

  const handleConfirmAddress = selectedAddress => {
    closeSuggestion()
    addDeliveryMethodAndContinue(replaceAddress(selectedAddress))
  }

  const onSelectTimeRange = id => {
    setContext(context => {
      return {
        ...context,
        orderInfo: { ...context.orderInfo, selectedTimeRange: id },
      }
    })
  }

  const changeRegion = async province => {
    try {
      const data = await getDeliveryMethods({
        orderId: context.orderInfo.id,
        returnId: context.orderInfo.returnId,
        regionId: province || selectedCountry?.id,
      })
      updateDeliveryMethods(data)

      updateContextShippingAddress(province)
      clearFieldErrors()

      setShowRegionModal(false)
      setSelectedProvince(province)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const updateContextShippingAddress = province => {
    setContext(context => {
      const updatedProvince = context?.allowed_region?.provinces?.find(p => p.id === province)
      const needsResetShippingAddress = deliveryMethod?.carrier_service?.is_connected

      const newContext = { ...context }
      newContext.orderInfo.shipping_address.province = updatedProvince

      //only done when the carrier is connected, as the form will not be displayed to the user,
      //and this will cause the form to be invalid and the user will not be able to fix it.
      if (needsResetShippingAddress) {
        newContext.orderInfo.shipping_address.postcode = ''
        newContext.orderInfo.shipping_address.line_1 = ''
        newContext.orderInfo.shipping_address.line_2 = ''
        newContext.orderInfo.shipping_address.locality = ''
      }

      return newContext
    })
  }

  const handleChangeCarbonOffset = checked => {
    setContext(context => ({
      ...context,
      orderInfo: { ...context.orderInfo, carbonOffset: checked },
    }))
  }

  const onSelectPickupDate = date => {
    setContext(context => ({
      ...context,
      orderInfo: { ...context.orderInfo, selectedPickupDate: date },
    }))
  }

  return (
    <Loading
      show={addingDeliveryMethod || assigningToReturnGroup || fetchingDeliveryMethods}
      messages={loadingMessages}
      height={335}
    >
      <PolicyRuleMessageWrapper>
        <PolicyRuleAlerts
          displayMessages={context.displayMessages}
          placement={policyRuleDisplayMessagePlacements.returnMethodPage}
          includeGlobalMessages
        />
      </PolicyRuleMessageWrapper>
      <Styles.Header>
        <p>{t('Escoge el método de envío que quieres usar para devolver los productos seleccionados')}</p>
        <DeliveryRegionInfo
          onClick={() => setShowRegionModal(true)}
          region={`${getSelectedProvince({ provinces: context?.allowed_region?.provinces, selectedProvinceId: selectedProvince })} ${selectedCountry?.name}`}
          disableChange={!context?.allowed_region?.provinces?.length > 0}
        />
      </Styles.Header>
      <DeliveryMethodSelector
        methods={deliveryMethods}
        onChange={handleChangeDeliveryMethod}
        deliveryMethodSelected={deliveryMethod}
        currency={context?.orderInfo?.currency}
        onChangeCarbonOffset={handleChangeCarbonOffset}
        carbonOffsetValue={context?.orderInfo?.carbonOffset}
      />
      <DeliveryMethodInfo
        deliveryMethod={deliveryMethod}
        message={deliveryMethodCustomMessage}
        onSelectTimeRange={onSelectTimeRange}
        specialConstraints={specialConstraints}
        onSelectPickupDate={onSelectPickupDate}
        provinces={provinces}
        shippingAddress={shippingAddress}
        updateAddress={handleUpdateAddress}
        orderInfo={context?.orderInfo}
        merchantInfo={context?.merchantInfo}
        showExchangeAddressCheckbox={showExchangeAddressCheckbox}
        useSameAddressForExchange={context.orderInfo.use_same_address_for_exchange}
        onChangeUseSameAddress={handleChangeUseSameAddress}
        fieldErrors={fieldErrors}
      />
      <DropOffPointSelector
        deliveryMethod={deliveryMethod}
        selectedDropOffPoint={selectedDropOffPoint}
        onOpen={openDropOffPointDrawer}
      />
      <ReturnLabelsSelector
        deliveryMethod={deliveryMethod}
        onChange={changeNumberOfReturnLabels}
        numberOfReturnLabels={numberOfReturnLabels}
        maxNumberOfReturnLabels={maxNumberOfReturnLabels}
      />
      <ReturnGroupOut
        returnGroup={context?.orderInfo?.return_group}
        onLeaveReturnGroup={leaveReturnGroup}
        availableGroupAction={availableGroupAction}
      />
      <ReturnGroupIn
        returnGroup={context?.orderInfo?.return_group}
        availableGroupAction={availableGroupAction}
        onJoinReturnGroup={joinReturnGroup}
      />
      <Summary />
      <ActionsContainer>
        <ButtonWithSpinner
          type="submit"
          fullWidth
          onClick={handleContinue}
          loading={addingDeliveryMethod || validatingAddress}
          loadingLabel={t('Procesando')}
          defaultLabel={t('Continuar')}
          disabled={
            addingDeliveryMethod ||
            validatingAddress ||
            !deliveryMethod ||
            (showDropOffPointSelector && !selectedDropOffPoint) ||
            !AddressFormValidations.isFormValid({
              deliveryMethod: deliveryMethod,
              address: context.orderInfo.shipping_address,
              timeRange: context.orderInfo.selectedTimeRange,
              isProvinceRequired: context?.allowed_region?.provinces?.length > 0,
              phone: getPhoneNumber(context?.orderInfo?.shipping_address?.phone),
              specialConstraints: specialConstraints,
              pickupDate: context.orderInfo.selectedPickupDate,
            })
          }
        />
      </ActionsContainer>
      <RegionsModal
        show={showRegionModal}
        provinces={getProvinces()}
        selectedCountry={selectedCountry?.name}
        selectedProvince={selectedProvince}
        onAccept={changeRegion}
        onClose={() => setShowRegionModal(false)}
        availableTransactionTypes={context?.orderInfo?.available_transaction_types}
        availableTransactionTypesForRegion={context?.orderInfo?.available_transaction_types_for_region}
        allowsUnifiedFlow={context?.merchantInfo?.preferences?.allows_unified_flow}
      />
      <ExchangeNotAllowedModal
        show={showExchangeNotAllowedModal}
        onClose={() => setShowExchangeNotAllowedModal(false)}
      />
      <DropOffPointDrawer
        show={showDropOffPointDrawer}
        orderId={context.orderInfo.id}
        returnId={context.orderInfo.returnId}
        deliveryMethod={deliveryMethod}
        shippingAddress={context.orderInfo.shipping_address}
        selectedDropOffPoint={selectedDropOffPoint}
        onConfirm={handleConfirmDropOffPoint}
        onClose={closeDropOffPointDrawer}
      />
      <AddressSuggestionDrawer
        show={showSuggestion}
        customerAddress={context.orderInfo.shipping_address}
        suggestedAddress={suggestedAddress}
        onConfirm={handleConfirmAddress}
        onClose={closeSuggestion}
      />
      <ConsentModal
        show={showConsentModal}
        consentModal={consentModal}
        onConfirm={confirmConsent}
        onClose={closeConsentModal}
      />
    </Loading>
  )
}

export default DeliveryMethods
